/**
 * ScorePopup — Diablo-grade floating damage numbers for every pop.
 *
 * Each pop spawns a gold/crimson +N number at the ball's position that floats
 * upward with physics-style arc, fades, and scales. Essentially the bread-and-
 * butter feedback language of AAA action games.
 *
 * Usage:
 *   ScorePopup.spawn(scene, x, y, 150);
 *   ScorePopup.spawn(scene, x, y, 2400, { color: '#FFD700', scale: 1.4 }); // milestone
 */

import Phaser from 'phaser';
import { FONTS, COLORS_HEX } from '@/ui/theme';

export interface ScorePopupOptions {
  color?: string;
  stroke?: string;
  scale?: number;
  duration?: number;
  risePx?: number;
  combo?: number;
  critical?: boolean;
}

export class ScorePopup {
  static spawn(scene: Phaser.Scene, x: number, y: number, amount: number, opts: ScorePopupOptions = {}): void {
    if (!scene || !scene.add) return;

    const baseScale = opts.scale ?? (amount >= 500 ? 1.4 : amount >= 200 ? 1.1 : 0.9);
    const color = opts.color ?? (opts.combo && opts.combo >= 3 ? '#FFD700' : amount >= 500 ? COLORS_HEX.goldLight : COLORS_HEX.textPrimary);
    const stroke = opts.stroke ?? '#050912';
    const duration = opts.duration ?? 720;
    const risePx = opts.risePx ?? (80 + Math.min(amount / 50, 60));
    const critical = opts.critical ?? (amount >= 500 || (opts.combo ?? 0) >= 5);

    const text = scene.add.text(x + (Math.random() - 0.5) * 40, y - 20, `+${amount.toLocaleString('en-US')}`, {
      fontFamily: FONTS.display,
      fontSize: `${Math.round(42 * baseScale)}px`,
      color,
      stroke,
      strokeThickness: 5,
      fontStyle: 'italic',
      shadow: { offsetX: 0, offsetY: 4, color: '#000000', blur: 10, fill: true },
    } as any);
    text.setOrigin(0.5);
    text.setDepth(8000);
    text.setScale(0.3);
    text.setAlpha(0);

    // Apply gold → pearl gradient on criticals
    if (critical) {
      try {
        const c = text.context as CanvasRenderingContext2D;
        const grad = c.createLinearGradient(0, 0, 0, text.height);
        grad.addColorStop(0, '#FFF4C2');
        grad.addColorStop(0.5, '#FFD700');
        grad.addColorStop(1, '#B87333');
        text.setTint(0xFFFFFF);
        (text.style as any).setFill(grad);
        text.updateText();
      } catch {}
    }

    // Entrance: scale up + fade in with slight arc
    const arcDX = (Math.random() - 0.5) * 80;
    scene.tweens.add({
      targets: text,
      alpha: 1,
      scale: baseScale,
      duration: 140,
      ease: 'Back.easeOut',
    });

    // Main rise + fade
    scene.tweens.add({
      targets: text,
      y: y - risePx,
      x: text.x + arcDX,
      scale: baseScale * 0.85,
      duration,
      ease: 'Quad.easeOut',
    });
    scene.tweens.add({
      targets: text,
      alpha: 0,
      delay: duration * 0.6,
      duration: duration * 0.4,
      ease: 'Quad.easeIn',
      onComplete: () => text.destroy(),
    });

    // Little bounce on criticals
    if (critical) {
      scene.tweens.add({
        targets: text,
        angle: { from: -6, to: 6 },
        duration: 120,
        yoyo: true,
        repeat: 1,
        delay: 140,
      });
    }
  }

  /**
   * Multi-kill word (DOUBLE / TRIPLE / MEGA / ULTRA POP) — flies in and out.
   */
  static spawnMultiKill(scene: Phaser.Scene, word: string, x: number, y: number, tier: 2 | 3 | 4 | 5): void {
    const fontSize = { 2: 72, 3: 88, 4: 104, 5: 120 }[tier];
    const color = { 2: '#FDBA74', 3: '#F59E0B', 4: '#F0ABFC', 5: '#FFF4C2' }[tier];
    const stroke = { 2: '#7C2D12', 3: '#5B3900', 4: '#7C3AED', 5: '#3D1E00' }[tier];

    const text = scene.add.text(x - 600, y, word, {
      fontFamily: FONTS.display,
      fontSize: `${fontSize}px`,
      color,
      stroke,
      strokeThickness: 10,
      fontStyle: 'italic',
      shadow: { offsetX: 0, offsetY: 8, color: '#000000', blur: 20, fill: true },
      letterSpacing: 4,
    } as any).setOrigin(0.5).setDepth(9000).setAlpha(0);

    // Fly in from off-screen left
    scene.tweens.add({
      targets: text,
      x,
      alpha: 1,
      duration: 220,
      ease: 'Back.easeOut',
    });

    // Hold, then fly out right
    scene.time.delayedCall(900, () => {
      scene.tweens.add({
        targets: text,
        x: x + 600,
        alpha: 0,
        scale: 1.3,
        duration: 260,
        ease: 'Quad.easeIn',
        onComplete: () => text.destroy(),
      });
    });
  }
}
